// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect, useSelector, useDispatch } from "react-redux";
import React, { useEffect, useCallback, useContext } from "react";
import * as R from "ramda";

import {
  Filter as StyledFilter,
  ListItem,
  SubHeading,
  Status as StyledStatus,
  Separator,
  PendingText,
  CompleteText,
  CancelledText,
  ArchivedText,
} from "./styles";
import {
  setManageViewFilter,
  filterEmbeddedFields,
} from "src/actions/workflows";
import { getChecklistFieldsById } from "src/reducers";
import Checkbox from "src/components/Checkbox";
import AllRecordsFilter from "./AllRecordsFilter";
import Name from "src/components/Status/Name";
import SelectAll from "./SelectAll";
import {
  getWorkflowStatus,
  getWorkflowPendingStatuses,
  getWorkflowCompleteStatuses,
  getCustomWorkflowStatuses,
  getAllRecords,
} from "src/reducers";
import { getAllRecordsLabel } from "src/utils/filters";
import { UpperCase } from "src/styles";
import { CANCELED } from "src/constants/status";
import { FilterWorkerContext } from "src/contexts/FilterWorkerContext";

import type { AppState, ColumnId, WorkflowId } from "src/types";

type Props = {
  column?: ColumnId,
  values: Array<{ id: number, active: boolean }>,
  parentRef: any,
  _setManageViewFilter: Function,
  handleClose: Function,
  pendingStatuses: Array<number>,
  completeStatuses: Array<number>,
  setFilter: Function,
  toggleSortBy: Function,
  filter: Array<any>,
  getCustomWorkflowStatuses: (workflowId: WorkflowId) => Array<any>,
};

const Status = ({
  column = "status",
  parentRef,
  values,
  _setManageViewFilter,
  handleClose,
  pendingStatuses,
  completeStatuses,
  setFilter,
  filter = [],
  toggleSortBy,
  getCustomWorkflowStatuses,
}: Props) => {
  const dispatch = useDispatch();

  const allRecordsColumnId = column.includes("-")
    ? column.split("-")[0]
    : column;

  const workflow = useSelector(({ app }) => app.workflow);
  const allRecords = useSelector(({ app }) => getAllRecords(app));
  const fieldsById = useSelector(({ app }) => getChecklistFieldsById(app));
  const filterWorker = useContext(FilterWorkerContext);

  const filters = workflow.instanceFilter;
  const chatroomAttributes = ["owner", "dueDate", "status"];
  const instancesById = workflow.instancesById;
  const updatedInstances = { ...instancesById };

  const fieldSettings = JSON.parse(
    // $FlowFixMe - flow doesn't support optional chaining
    fieldsById.get(allRecordsColumnId)?.get("settings") || "{}",
  );

  let workerData = {
    workflow,
    fieldsById: fieldsById.toJS(),
    instancesById,
    filters,
    chatroomAttributes,
    updatedInstances,
    allRecords,
  };

  const closeModal =
    ((event: any) => {
      if (event.keyCode === 13) {
        handleClose();
      }
    },
    [handleClose]);

  const clickOutside = useCallback(
    (event: any) => {
      if (parentRef && parentRef.current) {
        if (!parentRef.current.contains(event.target)) {
          handleClose();
        }
      }
    },
    [parentRef, handleClose],
  );

  useEffect(() => {
    // $FlowFixMe
    document.addEventListener("keydown", closeModal, false);
    document.addEventListener("click", clickOutside, false);
    return () => {
      // $FlowFixMe
      document.removeEventListener("keydown", closeModal, false);
      document.removeEventListener("click", clickOutside, false);
    };
  }, []);

  const handleSort = (ascending: boolean) => {
    if (ascending) {
      toggleSortBy(false, true);
    } else {
      toggleSortBy(true, true);
    }
    handleClose();
  };

  const handleSelect = useCallback(
    (event: Event, item: number | string) => {
      event.preventDefault();
      event.stopPropagation();

      if (R.includes(item, filter)) {
        // $FlowFixMe
        const status = R.reject(R.equals(item), filter);
        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: status,
          },
        };

        setFilter(column, status);
      } else {
        const status = [...filter, item];

        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: status,
          },
        };

        if (filterWorker) {
          filterWorker.postMessage(workerData, (instances) => {
            dispatch(filterEmbeddedFields({ instances }));
          });
        }

        setFilter(column, status);
      }
    },
    [filter, _setManageViewFilter],
  );

  const handleIncludingCancelled = (event) => {
    if (filter.length === 0) {
      onSelectAll();
    } else {
      handleSelect(event, -3);
    }
  };

  const handleIncludingArchived = (event) => {
    handleSelect(event, "includeArchived");
  };

  const mappedToPending = R.difference(pendingStatuses, filter).length == 0;
  const mappedToComplete = R.difference(completeStatuses, filter).length == 0;

  // Get the custom workflow statues from store, if status is an embedded field
  const statuses = column.includes("-")
    ? (getCustomWorkflowStatuses(fieldSettings.workflow) ?? []).map(
        R.prop("id"),
      ) || []
    : values.map(R.prop("id"));

  const ids = [...statuses, CANCELED];

  const handleMappedToStatus = (
    mappedStatuses: Array<number>,
    active: boolean,
  ) => {
    if (active) {
      const status = R.difference(filter, mappedStatuses);

      setFilter(column, status);
    } else {
      const status = R.union(filter, mappedStatuses);

      setFilter(column, status);
    }
  };

  const onSelectAll = () => {
    setFilter(column, ids);
  };

  const onSelectNone = () => {
    setFilter(column, []);
  };

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <StyledFilter onClick={preventDefault}>
      <SubHeading>{i18n.t(k.SORT_BY2)}</SubHeading>
      <ul>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(true)}>
          {i18n.t(k.ASCENDING)}
        </ListItem>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(false)}>
          {i18n.t(k.DESCENDING)}
        </ListItem>
      </ul>
      <Separator />
      <SubHeading>{i18n.t(k.FILTER)}</SubHeading>
      <SelectAll onSelectAll={onSelectAll} onSelectNone={onSelectNone} />
      <StyledStatus
        onClick={() => handleMappedToStatus(pendingStatuses, mappedToPending)}
      >
        <Checkbox checked={mappedToPending} />
        <span>
          {i18n.t(k.MAPPED_TO)} <PendingText>{i18n.t(k.PENDING)}</PendingText>
        </span>
      </StyledStatus>
      <StyledStatus
        onClick={() => handleMappedToStatus(completeStatuses, mappedToComplete)}
      >
        <Checkbox checked={mappedToComplete} />
        <span>
          {i18n.t(k.MAPPED_TO)}{" "}
          <CompleteText>{i18n.t(k.COMPLETE)}</CompleteText>
        </span>
      </StyledStatus>
      <StyledStatus onClick={handleIncludingArchived}>
        <Checkbox checked={R.includes("includeArchived", filter)} />
        <span>
          {i18n.t(k.INCLUDE)} <ArchivedText>{i18n.t(k.ARCHIVED)}</ArchivedText>
        </span>
      </StyledStatus>
      <StyledStatus onClick={handleIncludingCancelled}>
        <Checkbox checked={R.includes(-3, filter)} />
        <span>
          {i18n.t(k.INCLUDE)}{" "}
          <CancelledText>{i18n.t(k.CANCELLED)}</CancelledText>
        </span>
      </StyledStatus>
      <Separator />
      {column.includes("-") && (
        <AllRecordsFilter
          label={`${i18n.t(k.ALL)} ${getAllRecordsLabel(
            allRecordsColumnId,
            fieldsById,
          )} ${i18n.t(k.RECORDS)}`}
          isAllRecords={allRecords[allRecordsColumnId]}
          columnId={allRecordsColumnId}
          workerData={workerData}
        />
      )}
      <ul>
        {ids.map((value) => (
          <StyledStatus
            tabIndex="0"
            role="button"
            key={value}
            onClick={(event) => handleSelect(event, value)}
          >
            <Checkbox
              id={`${i18n.t(k.STATUS1)}${value}`}
              checked={R.includes(value, filter)}
              handleChange={(event) => handleSelect(event, value)}
            />

            <UpperCase>
              <Name id={value} />
            </UpperCase>
          </StyledStatus>
        ))}
      </ul>
    </StyledFilter>
  );
};

const mapStateToProps = ({ app }: { app: AppState }, props) => ({
  values: getWorkflowStatus(app, props.id),
  getCustomWorkflowStatuses: getCustomWorkflowStatuses(app),
  pendingStatuses: getWorkflowPendingStatuses(app, props.id),
  completeStatuses: getWorkflowCompleteStatuses(app, props.id),
});

export default connect(mapStateToProps, {
  _setManageViewFilter: setManageViewFilter,
})(Status);
